import { useEffect, useState } from 'react';

import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { Link, Typography } from '@mui/material';

import AccessDenied from '@/components/shared/AccessDenied';
import Loading from '@/components/utils/Loading';
import { email } from '@/config';
import {
  type UserAccountInviteApproveMutation,
  type UserAccountInviteDeclineMutation,
  useAccountUserByEmailLazyQuery,
  useOrganizationUserInviteApproveMutation,
  useOrganizationUserInviteDeclineMutation,
  useUserAccountInviteApproveMutation,
  useUserAccountInviteDeclineMutation,
} from '@/generated';
import { useSession } from '@/providers/SessionProvider/SessionProvider';
import { useSnackbar } from '@/providers/SnackbarProvider/SnackbarProvider';

import { INVITATION_FAILURE_MESSAGE } from './constants';

const titleMap = {
  accept: 'Invite Accepted',
  decline: 'Invite Declined',
} as const satisfies Record<AccessRequest, string>;

type AccessRequest = 'accept' | 'decline';
type MutationStatus = 'pending' | 'success' | 'failure';

const AccessRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const { currentClient } = useSession();
  const queryParams = new URLSearchParams(location.search);
  const snackbar = useSnackbar();

  // TODO: feature_flag_cleanup: hasUserLifecycle
  const hasUserLifecycle = !!currentClient?.hasUserLifecycle;

  const [lookupUser] = useAccountUserByEmailLazyQuery();
  const [approveRequest] = useOrganizationUserInviteApproveMutation();
  const [declineRequest] = useOrganizationUserInviteDeclineMutation();
  const [approveAccountInvite] = useUserAccountInviteApproveMutation();
  const [declineAccountInvite] = useUserAccountInviteDeclineMutation();
  const [mutationStatus, setMutationStatus] =
    useState<MutationStatus>('pending');

  const access = (queryParams.get('action') as AccessRequest) ?? '';
  const emailParam = queryParams.get('email');
  const accountIdParam = queryParams.get('account_id');
  const accountNameParam = queryParams.get('account_name');
  const emailAddress = emailParam ? atob(emailParam) : '';
  const accountId = accountIdParam ?? '';
  const accountName = accountNameParam ? atob(accountNameParam) : '';

  useEffect(() => {
    const handleError = (message: string) => {
      setMutationStatus('failure');
      snackbar({
        message,
        severity: 'error',
      });
    };

    const handleApproveOrDecline = async () => {
      if (!access || !emailAddress || !accountId) {
        handleError(INVITATION_FAILURE_MESSAGE);
        return;
      }

      const isApproval = access === 'accept';

      if (hasUserLifecycle) {
        const { data, error } = await lookupUser({
          variables: { accountId, emailAddress },
        });

        if (error) {
          handleError(error.message);
          return;
        }

        const userId = data?.accountUserByEmail?.id;
        if (!userId) {
          handleError(INVITATION_FAILURE_MESSAGE);
          return;
        }

        const run = isApproval ? approveRequest : declineRequest;

        run({
          variables: { userId },
          onCompleted: () => {
            setMutationStatus('success');
          },
          onError: (e: Error) => handleError(e.message),
        });
      } else {
        const run = isApproval ? approveAccountInvite : declineAccountInvite;

        const handleLegacyCompleted = (
          data:
            | UserAccountInviteApproveMutation
            | UserAccountInviteDeclineMutation,
        ) => {
          const mutation = isApproval
            ? (data as UserAccountInviteApproveMutation)
                .userAccountInviteApprove
            : (data as UserAccountInviteDeclineMutation)
                .userAccountInviteDecline;

          if (mutation.success) {
            setMutationStatus('success');
          } else {
            handleError(INVITATION_FAILURE_MESSAGE);
          }
        };

        run({
          variables: { emailAddress, accountId },
          onCompleted: handleLegacyCompleted,
          onError: (e: Error) => handleError(e.message),
        });
      }
    };

    if (access === 'accept' || access === 'decline') {
      handleApproveOrDecline();
    }
    // Mutation and snackbar callbacks only process the current invite request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access, emailAddress, accountId]);

  const descriptionMap = {
    //todo accept copy to be modified once we have a new design for this page
    accept: (
      <>
        You accepted the invite request for{' '}
        <Typography component="span" fontWeight={600}>
          {emailAddress}
        </Typography>{' '}
        to join{' '}
        <Typography component="span" fontWeight={600}>
          {accountName}
        </Typography>{' '}
        account. An invitation email has been sent to them.
      </>
    ),
    decline: (
      <>
        You declined the invite request for{' '}
        <Typography component="span" fontWeight={600}>
          {emailAddress}
        </Typography>{' '}
        to join{' '}
        <Typography component="span" fontWeight={600}>
          {accountName}
        </Typography>{' '}
        account. If you believe this is an error, reach out to your
        organization’s administrator or contact{' '}
        <Link href={`mailto:${email}`} sx={{ color: 'primary.main' }}>
          {email}
        </Link>{' '}
        for help.
      </>
    ),
  } as const satisfies Record<AccessRequest, JSX.Element>;

  const isValidAction = access === 'accept' || access === 'decline';
  const hasSuccessfulAction = isValidAction && mutationStatus === 'success';
  const isPendingAction = isValidAction && mutationStatus === 'pending';
  if (isPendingAction) {
    return <Loading height="100vh" width="100vw" />;
  }

  const title = hasSuccessfulAction ? titleMap[access] : 'Invalid Request';

  const description = hasSuccessfulAction ? (
    descriptionMap[access]
  ) : (
    <>
      The request parameters are invalid or missing. If you believe this is an
      error, reach out to your organization’s administrator or contact{' '}
      <Link href={`mailto:${email}`} sx={{ color: 'primary.main' }}>
        {email}
      </Link>{' '}
      for help.
    </>
  );

  return (
    <AccessDenied
      title={title}
      description={description}
      actionButtons={{
        primary: {
          label: 'Go Back',
          onClick: () => navigate(-1),
        },
        secondary: {
          label: 'Log Out',
          onClick: () => logout(),
        },
      }}
    />
  );
};

export default AccessRequest;
